import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, Collapse, Container, Stack, Step, StepLabel, Stepper, useMediaQuery, useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { usePostSignup } from '@/api/userAccount';
import logoBlue from '@/assets/sofar-logo-blue.svg';
import logo from '@/assets/sofar-logo-white.png';
import { AccountStep } from '@/components/signup/AccountStep';
import { ContactInfoStep } from '@/components/signup/ContactInfoStep';
import { LocationInfoStep } from '@/components/signup/LocationInfoStep';
import { ReviewStep } from '@/components/signup/ReviewStep';
import { getPasswordPolicyMessages } from '@/utils/passwordPolicyHelpers';

type SignUpServerErrorResponse = {
    // This is how errors in the fields are returned from the API
    // This probably won't happen because the form is validated on the client side
    data: { data: { errors: { param: string; msg: string }[] } };
};
type SignUpServerError = {
    code: string;
    message: string;
    response: SignUpServerErrorResponse;
};

const steps = ['Contact Info', 'Location Info', 'Account Setup', 'Review'];

function getStepContent(step: number) {
    switch (step) {
        case 0:
            return <ContactInfoStep />;
        case 1:
            return <LocationInfoStep />;
        case 2:
            return <AccountStep />;
        case 3:
            return <ReviewStep />;
        default:
            throw new Error('Unknown step');
    }
}

interface IFormInput {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    username: string;
    password: string;
    reEnterPassword: string;
    zipcode: string;
    state: string;
    country: string;
    organization: string;
    termsAndConditions: boolean;
}

const signupFormDefaultValues: IFormInput = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    username: '',
    password: '',
    reEnterPassword: '',
    zipcode: '',
    state: '',
    country: '',
    organization: '',
    termsAndConditions: false,
};

export const SignUp = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchParams] = useSearchParams();
    const [activeStep, setActiveStep] = useState(0);
    const { mutateAsync: postSignup, isError, error, data } = usePostSignup();
    const passwordPolicyMessages = getPasswordPolicyMessages(error);
    const mixpanel = useMixpanel();
    const formMethods = useForm<IFormInput>({
        defaultValues: signupFormDefaultValues,
        mode: 'onChange',
    });

    const handleNext = async () => {
        const isValid = await formMethods.trigger();
        if (isValid) {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    const onSignupSubmit = async (data: IFormInput) => {
        try {
            const signupResult = await postSignup(data);
            if (signupResult.status === 'success') {
                mixpanel?.identify(data.username);
                mixpanel?.track('Sign Up Success', {
                    username: data.username,
                });
                return setActiveStep(activeStep + 1);
            }
        } catch (error) {
            mixpanel?.track('Sign Up Error', {
                username: data.username,
                message: (error as Error)?.message,
            });
            console.error('error while attempting to sign up', error);
        }
    };

    return (
        <Grid container component="main" sx={{ height: '100vh' }}>
            <Grid size={{ xs: 12, sm: 9, md: 7 }} component={Paper} elevation={6} square>
                <Box
                    sx={{
                        my: 8,
                        mx: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Container
                        maxWidth="sm"
                        sx={{
                            display: { xs: 'flex', sm: 'none' },
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ height: 50 }} component="img" src={logoBlue} alt="sofar ocean logo" />
                    </Container>
                    <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h3">
                        Spotter Dashboard Sign Up
                    </Typography>
                    <Grid container spacing={2} component="form" noValidate sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12 }} sx={{ overflowX: 'hidden' }}>
                            <Stepper
                                activeStep={activeStep}
                                sx={{ pt: 3, pb: 5 }}
                                orientation={isMobile ? 'vertical' : 'horizontal'}
                            >
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            {activeStep === steps.length ? (
                                <Stack
                                    spacing={2}
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
                                >
                                    <Stack
                                        spacing={2}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            width: '100%',
                                        }}
                                    >
                                        <Alert severity="success">
                                            <span>{data?.message}</span>
                                        </Alert>
                                        <Alert severity="success">
                                            Thank you for signing up! Everything is ready for you to login with your new
                                            account.
                                        </Alert>
                                    </Stack>
                                    <Button
                                        component={RouterLink}
                                        to={{
                                            pathname: '/login',
                                            search: searchParams.toString(),
                                        }}
                                        variant="contained"
                                        endIcon={<ArrowForwardIosIcon />}
                                    >
                                        Login
                                    </Button>
                                </Stack>
                            ) : (
                                <>
                                    <FormProvider {...formMethods}>{getStepContent(activeStep)}</FormProvider>
                                    <Stack spacing={2}>
                                        <Collapse in={isError} sx={{ width: '100%' }}>
                                            {passwordPolicyMessages.length === 0 && (
                                                <Alert severity="error" sx={{ my: 1 }}>
                                                    {(error as SignUpServerError)?.message}
                                                </Alert>
                                            )}

                                            {((error as SignUpServerError)?.response?.data?.data?.errors || []).map(
                                                (serverErr, index: number) => (
                                                    <Alert
                                                        key={index}
                                                        severity="error"
                                                        sx={{ my: 1 }}
                                                    >{`Error in ${serverErr.param}: ${serverErr.msg}`}</Alert>
                                                )
                                            )}
                                            {passwordPolicyMessages.map((message, index) => (
                                                <Alert key={`${index}-${message}`} severity="error" sx={{ my: 1 }}>
                                                    {message}
                                                </Alert>
                                            ))}
                                        </Collapse>
                                    </Stack>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        {activeStep !== 0 && (
                                            <Button onClick={handleBack} sx={{ mt: 3, ml: 1 }}>
                                                Back
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            onClick={
                                                activeStep === steps.length - 1
                                                    ? formMethods.handleSubmit(onSignupSubmit)
                                                    : handleNext
                                            }
                                            sx={{ mt: 3, ml: 1 }}
                                        >
                                            {activeStep === steps.length - 1 ? 'Sign up' : 'Next'}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Grid>
                        <Grid>
                            <Link
                                variant="body2"
                                component={RouterLink}
                                to={{
                                    pathname: '/login',
                                    search: searchParams.toString(),
                                }}
                            >
                                Already have an account? Sign in
                            </Link>
                        </Grid>
                        <Container sx={{ mt: 5 }}>
                            <Typography variant="body2" color="text.secondary" align="center">
                                {'Copyright © '}
                                <Link color="text.secondary" href="https://sofarocean.com/">
                                    Sofar Ocean
                                </Link>{' '}
                                {new Date().getFullYear()}
                                {'.'}
                            </Typography>
                        </Container>
                    </Grid>
                </Box>
            </Grid>
            <Grid
                size={{ xs: false, sm: 3, md: 5 }}
                sx={{
                    backgroundImage: `url(${logo}), url(https://filedn.com/lTTdn1W2IjNme17D5yWuF74/Resources/waves-2.jpg)`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '70%, cover',
                    backgroundPosition: '25% 10%, 35%',
                }}
            />
        </Grid>
    );
};
